import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AddCircleOutline, Edit, Send, CheckCircle, Cancel, Email,
  Visibility, ThumbUp, ThumbDown, ContentCopy, HelpOutline, SwapHoriz, Undo,
} from '@mui/icons-material';
import { getQuoteEvents } from '../../../services/quoteService';
import type { QuoteEvent } from '../../../types/quote';

interface QuoteActivityLogProps {
  quoteKey: string;
}

const EVENT_CONFIG: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  Created:              { icon: AddCircleOutline, color: '#9e9e9e', label: 'Created' },
  Updated:              { icon: Edit,             color: '#757575', label: 'Updated' },
  SubmittedForApproval: { icon: Send,             color: '#7b1fa2', label: 'Submitted for Approval' },
  Approved:             { icon: CheckCircle,      color: '#2e7d32', label: 'Approved' },
  Rejected:             { icon: Cancel,           color: '#c62828', label: 'Rejected' },
  Sent:                 { icon: Email,            color: '#1565c0', label: 'Sent to Client' },
  LinkOpened:           { icon: Visibility,       color: '#0288d1', label: 'Client Viewed' },
  Accepted:             { icon: ThumbUp,          color: '#2e7d32', label: 'Accepted' },
  Declined:             { icon: ThumbDown,        color: '#c62828', label: 'Declined by Client' },
  ChangesRequested:     { icon: Edit,             color: '#e65100', label: 'Client Requested Changes' },
  Duplicated:           { icon: ContentCopy,      color: '#616161', label: 'Duplicated' },
  // Manual override (2026-07-24): admin forced the status out-of-band.
  StatusOverridden:     { icon: SwapHoriz,        color: '#e65100', label: 'Status Overridden' },
  // 2026-09-11: the converted-to work order was deleted — link cleared, back to Accepted.
  ConversionReverted:   { icon: Undo,             color: '#e65100', label: 'Conversion Reverted' },
};

const formatTimestamp = (ts: string): string => {
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' at '
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

export const QuoteActivityLog = ({ quoteKey }: QuoteActivityLogProps) => {
  const theme = useTheme();
  const [events, setEvents] = useState<QuoteEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getQuoteEvents(quoteKey)
      .then((data) => { if (!cancelled) setEvents(data); })
      .catch(() => { if (!cancelled) setEvents([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [quoteKey]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  }

  if (events.length === 0) {
    return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No activity recorded.</Typography>;
  }

  const sorted = [...events].sort((a, b) => new Date(b.Timestamp).getTime() - new Date(a.Timestamp).getTime());

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Activity Log</Typography>
      <Box sx={{ position: 'relative', pl: 3 }}>
        {/* Vertical line */}
        <Box sx={{
          position: 'absolute', left: 10, top: 4, bottom: 4, width: 2,
          bgcolor: theme.palette.divider,
        }} />

        {sorted.map((event) => {
          const cfg = EVENT_CONFIG[event.EventType] || { icon: HelpOutline, color: '#9e9e9e', label: event.EventType };
          const Icon = cfg.icon;
          return (
            <Box key={event.QuoteEventKey} sx={{ position: 'relative', mb: 2.5, minHeight: 28 }}>
              {/* Icon dot */}
              <Box sx={{
                position: 'absolute', left: -23, top: 0, width: 24, height: 24,
                borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(cfg.color, 0.12), zIndex: 1,
              }}>
                <Icon sx={{ fontSize: 14, color: cfg.color }} />
              </Box>

              {/* Content */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', color: cfg.color }}>
                  {cfg.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {event.UserId} &middot; {formatTimestamp(event.Timestamp)}
                </Typography>
                {event.Details && (
                  <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.78rem', color: theme.palette.text.secondary }}>
                    {event.Details}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
